/**
 * Whether this session has SERVER real-time streaming — the one decision.
 *
 * Server streaming (the AppSync socket that pushes transactions, address
 * watches, catalog and group changes, notifications and collaboration) is a
 * plan feature: `realtime_streaming`. Two things must both be true for it:
 *
 *  1. the build wires a stream endpoint (`isStreamingConfigured`), and
 *  2. the caller's plan grants the entitlement, overrides layered on — through
 *     `grantedEntitlements`, the same arithmetic `<DashboardPlan>` and the
 *     authorizer use, so the socket and the controls cannot disagree.
 *
 * `<DashboardStreamingProvider>` is the only place that decides with this: it
 * creates the server client or does not, and publishes the verdict below.
 * Every surface asks {@link useServerStreaming} — never the static config flag,
 * which knows about the build and nothing about the customer (a guard test
 * holds that line). Cross-tab messaging inside the browser (the resource-change
 * hub, the collaboration broadcast leg) is NOT server streaming and keeps
 * working on every plan.
 *
 * The plan read's three states map as the rest of the app maps them:
 *
 *  - FAILED (or a plan this build does not know): unknown, and unknown grants
 *    everything — a slow subscriptions API must not strip a paying tenant. The
 *    backend refuses subscribe/publish for a plan without the entitlement.
 *  - LOADING: also offered to the UI (the same "unknown grants" rule the
 *    `PlanProvider` gates follow), but the socket is not opened yet — see
 *    `connect`. Opening it and dropping it a moment later is the one default
 *    here that is expensive to undo.
 *  - A plan without the entitlement: not available, reason `"plan"`. Hidden
 *    means hidden — no upgrade prompt anywhere.
 *
 * The client can still be absent while `available` is true (the plan read is
 * in flight, a token refresh paused delivery), so anything that reads the
 * connection keeps its own `useOptionalStreamingClient()` check as well.
 */
import * as React from "react";
import { ENTITLEMENT, grantedEntitlements, type Plan } from "@authe/fe-commons/plans";
import { isStreamingConfigured } from "./streaming";
import { useCurrentPlan, type PlanReadStatus } from "./currentPlan";

/** Why server streaming is not available. */
export type ServerStreamingReason = "plan" | "not_configured";

/** The verdict every surface reads. */
export interface ServerStreaming {
  /** Offer live controls and indicators. */
  available: boolean;
  /** Present exactly when `available` is false. */
  reason?: ServerStreamingReason;
}

/** The provider's view: the verdict plus whether to open the socket now. */
export interface ServerStreamingDecision extends ServerStreaming {
  /** Create the server client now. False while the plan read is pending. */
  connect: boolean;
}

export interface ServerStreamingInput {
  /** The build wires a stream endpoint. */
  configured: boolean;
  /** The resolved plan, or `null` when unknown (failed read, unknown tier). */
  plan: Plan | null;
  /** The subscription's raw entitlement overrides. */
  overrides?: Record<string, unknown>;
  /** Whether the plan read has answered. */
  planStatus: PlanReadStatus;
}

const AVAILABLE: ServerStreaming = Object.freeze({ available: true });
const NOT_CONFIGURED: ServerStreaming = Object.freeze({ available: false, reason: "not_configured" });
const NOT_IN_PLAN: ServerStreaming = Object.freeze({ available: false, reason: "plan" });

/** The decision, pure. */
export function decideServerStreaming(input: ServerStreamingInput): ServerStreamingDecision {
  if (!input.configured) return { ...NOT_CONFIGURED, connect: false };
  if (
    input.plan &&
    !grantedEntitlements(input.plan, input.overrides ?? {}).has(ENTITLEMENT.REALTIME_STREAMING)
  ) {
    return { ...NOT_IN_PLAN, connect: false };
  }
  return { ...AVAILABLE, connect: input.planStatus === "settled" };
}

/** The shared, frozen verdict object for a decision — stable context identity. */
export function serverStreamingOf(decision: ServerStreaming): ServerStreaming {
  if (decision.available) return AVAILABLE;
  return decision.reason === "plan" ? NOT_IN_PLAN : NOT_CONFIGURED;
}

/** For the provider: decide from this session's plan read and the build. */
export function useServerStreamingDecision(): ServerStreamingDecision {
  const { plan, overrides, status } = useCurrentPlan();
  return React.useMemo(
    () =>
      decideServerStreaming({ configured: isStreamingConfigured, plan, overrides, planStatus: status }),
    [plan, overrides, status],
  );
}

export const ServerStreamingContext = React.createContext<ServerStreaming | null>(null);
ServerStreamingContext.displayName = "ServerStreamingContext";

/**
 * Is server real-time streaming part of this session?
 *
 * Outside the provider (signed out, or a test with no provider) only the build
 * is known, so the plan is treated as unknown — which grants.
 */
export function useServerStreaming(): ServerStreaming {
  const published = React.useContext(ServerStreamingContext);
  return published ?? (isStreamingConfigured ? AVAILABLE : NOT_CONFIGURED);
}
